import React from 'react';
import { Button, Card, Descriptions, Popconfirm, Space, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { ROUTES } from '@/constants/routes';
import { formatDateTime } from '@/utils/format';
import {
  ORDER_ACCOUNTING_DOCUMENT_META,
  type OrderAccountingDocumentType,
} from './order-accounting-documents';

const { Text } = Typography;

/**
 * Tham chiếu chứng từ KẾ TOÁN NỘI BỘ phát sinh từ đơn — KHÔNG phải chứng từ hệ đích
 * (AMIS/MISA/FAST…). Chứng từ hệ đích gắn với hoá đơn chứ không gắn với đơn, nằm ở cột
 * "Chứng từ tham chiếu" của `/sales/documents`.
 *
 * Đọc THẲNG `accounting.documents[]` mà BE đã denormalize sẵn — không join, không suy diễn
 * từ các field phẳng (chúng là khoá nghiệp vụ của BE, không phải dữ liệu hiển thị).
 */
export type OrderAccountingDocType = Exclude<OrderAccountingDocumentType, 'stock_transfer'> | 'journal_entry';

export interface OrderAccountingDocRef {
  type: OrderAccountingDocType;
  doc_id: string;
  code?: string | null;
  status?: string | null;
  doc_date?: string | null;
}

export interface OrderAccountingInfo {
  documents?: OrderAccountingDocRef[] | null;
  sales_invoice_error?: string | null;
}

export interface PosFulfillmentDocumentRefs {
  goods_issue_id?: string | null;
  goods_issue_code?: string | null;
  transit_goods_issue_id?: string | null;
  transit_goods_issue_code?: string | null;
}

/** Hoá đơn điện tử là block RIÊNG trên đơn (`orders.e_invoice`), không nằm trong `accounting`. */
export interface OrderEInvoiceInfo {
  requested?: boolean | null;
  invoice_id?: string | null;
  internal_invoice_number?: string | null;
  invoice_number?: string | null;
  status?: 'draft' | 'pending' | 'issued' | 'cancelled' | null;
}

/** Trang chi tiết theo loại chứng từ. Loại không có route thì hiện mã trần, không link chết. */
const DOC_HREF: Partial<Record<OrderAccountingDocType, (id: string) => string>> = {
  sales_invoice: ORDER_ACCOUNTING_DOCUMENT_META.sales_invoice.href,
  cash_receipt: ORDER_ACCOUNTING_DOCUMENT_META.cash_receipt.href,
  goods_issue: ORDER_ACCOUNTING_DOCUMENT_META.goods_issue.href,
  journal_entry: (id) => ROUTES.accounting.reportJournalEntryDetail(id),
  e_invoice: ORDER_ACCOUNTING_DOCUMENT_META.e_invoice.href,
};

type RowType = OrderAccountingDocType | 'stock_transfer';

const ROWS: { type: RowType; label: string; pending: string }[] = [
  { type: 'sales_invoice', label: ORDER_ACCOUNTING_DOCUMENT_META.sales_invoice.label, pending: 'Chờ tạo chứng từ bán hàng' },
  { type: 'goods_issue', label: ORDER_ACCOUNTING_DOCUMENT_META.goods_issue.label, pending: 'Chưa xuất kho' },
  { type: 'stock_transfer', label: ORDER_ACCOUNTING_DOCUMENT_META.stock_transfer.label, pending: 'Chưa có phiếu chuyển kho' },
  { type: 'e_invoice', label: ORDER_ACCOUNTING_DOCUMENT_META.e_invoice.label, pending: 'Chưa yêu cầu xuất hoá đơn' },
  { type: 'cash_receipt', label: ORDER_ACCOUNTING_DOCUMENT_META.cash_receipt.label, pending: 'Chưa có phiếu thu' },
  { type: 'journal_entry', label: 'Bút toán', pending: 'Chờ ghi sổ' },
];

const E_INVOICE_PENDING_TEXT: Record<string, string> = {
  pending: 'Chờ phát hành Hoá đơn điện tử',
  issued: 'Đã xuất, chưa có số',
  cancelled: 'Đã huỷ',
};

function Pending({ text }: { text: string }): React.ReactElement {
  return <Text style={{ color: '#ad4e00' }}>{text}</Text>;
}

/** Mở tab mới: người dùng đang soi đơn, không muốn mất ngữ cảnh khi xem chứng từ. */
function DocCode({ doc }: { doc: OrderAccountingDocRef }): React.ReactElement {
  const label = doc.code || '(không có số)';
  const href = doc.code ? DOC_HREF[doc.type]?.(doc.doc_id) : undefined;
  if (!href) return <Text strong>{label}</Text>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
      {label}
    </a>
  );
}

export const OrderAccountingCard: React.FC<{
  accounting?: OrderAccountingInfo | null;
  eInvoice?: OrderEInvoiceInfo | null;
  /** Mã phiếu xuất kho dự phòng của đơn ECom (`stock_issue.document_number`). */
  stockIssueCode?: string | null;
  /** POS fulfillment documents are denormalized on the order, outside accounting.documents. */
  posFulfillmentDocuments?: PosFulfillmentDocumentRefs | null;
  stockTransferId?: string | null;
  stockTransferCode?: string | null;
  carrierReconciliation?: { completed: boolean; occurredAt?: string | null; statementId?: string | null };
  accountingStatus?: string | null;
  accountingError?: string | null;
  isOffPlatform?: boolean;
  onRetryAccounting?: () => void | Promise<void>;
  isRetryingAccounting?: boolean;
  goodsIssueCostingStatus?: string | null;
  goodsIssueValuationMethod?: string | null;
  onRequestEInvoice?: () => void;
  isRequestingEInvoice?: boolean;
  /** Document Scenario Engine (CTBH/PXK/HĐĐT nháp) has a retryable failure logged — independent of `accountingStatus`/`onRetryAccounting`, which track the older event-replay accounting pipeline. */
  scenarioRetryable?: boolean;
  onRetryScenario?: () => void | Promise<void>;
  isRetryingScenario?: boolean;
}> = ({
  accounting,
  eInvoice,
  stockIssueCode,
  posFulfillmentDocuments,
  stockTransferId,
  stockTransferCode,
  carrierReconciliation,
  accountingStatus,
  accountingError,
  isOffPlatform = false,
  onRetryAccounting,
  isRetryingAccounting = false,
  goodsIssueCostingStatus,
  goodsIssueValuationMethod,
  onRequestEInvoice,
  isRequestingEInvoice,
  scenarioRetryable = false,
  onRetryScenario,
  isRetryingScenario = false,
}) => {
  const documents = accounting?.documents ?? [];
  const posGoodsIssueId = posFulfillmentDocuments?.goods_issue_id
    ?? posFulfillmentDocuments?.transit_goods_issue_id;
  const posGoodsIssueCode = posFulfillmentDocuments?.goods_issue_code
    ?? posFulfillmentDocuments?.transit_goods_issue_code;
  const isCostingDeferred = accountingStatus !== 'failed'
    && goodsIssueCostingStatus === 'pending'
    && goodsIssueValuationMethod === 'periodic_weighted_average';
  const canRetry = isOffPlatform && accountingStatus === 'failed' && !isCostingDeferred;
  const canRetryScenario = isOffPlatform && scenarioRetryable;

  const renderRow = (row: (typeof ROWS)[number]): React.ReactNode => {
    if (row.type === 'stock_transfer') {
      if (!stockTransferCode) return <Pending text={row.pending} />;
      if (!stockTransferId) return <Text strong>{stockTransferCode}</Text>;
      return (
        <a
          href={ORDER_ACCOUNTING_DOCUMENT_META.stock_transfer.href(stockTransferId)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontWeight: 600 }}
        >
          {stockTransferCode}
        </a>
      );
    }

    const matched = documents.filter((d) => d.type === row.type);
    if (matched.length) {
      // Một đơn có thể sinh nhiều chứng từ cùng loại (hoá đơn điều chỉnh, thu nhiều lần).
      return (
        <Space direction="vertical" size={2}>
          {matched.map((doc) => (
            <DocCode key={`${doc.type}-${doc.doc_id}`} doc={doc} />
          ))}
          {row.type === 'journal_entry' && isCostingDeferred && (
            <Pending text="Chờ Costing Run Bình quân cuối kỳ" />
          )}
        </Space>
      );
    }

    if (row.type === 'journal_entry' && isCostingDeferred) {
      return <Pending text="Chờ Costing Run Bình quân cuối kỳ" />;
    }

    if (row.type === 'e_invoice') {
      const eInvoiceId = eInvoice?.invoice_id ?? undefined;
      const internalInvoiceNumber = eInvoice?.internal_invoice_number?.trim() || undefined;
      const eInvoiceHref = eInvoiceId
        ? ORDER_ACCOUNTING_DOCUMENT_META.e_invoice.href(eInvoiceId)
        : undefined;
      if (eInvoice?.status === 'cancelled') {
        return (
          <Space direction="vertical" size={2}>
            <Pending text={E_INVOICE_PENDING_TEXT.cancelled} />
            {eInvoiceHref && (
              <a href={eInvoiceHref} title={eInvoiceId}>
                Mã nội bộ: {internalInvoiceNumber ?? '--'}
              </a>
            )}
          </Space>
        );
      }
      if (eInvoice?.invoice_number) {
        return eInvoiceHref ? (
          <a href={eInvoiceHref} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
            {eInvoice.invoice_number}
          </a>
        ) : <Text strong>{eInvoice.invoice_number}</Text>;
      }
      const text = eInvoice?.status === 'issued'
        ? E_INVOICE_PENDING_TEXT.issued
        : eInvoice?.invoice_id
          ? 'Đã tạo hóa đơn nháp · Chưa phát hành'
        : eInvoice?.requested
            ? 'Đang xử lý yêu cầu xuất hóa đơn'
            : row.pending;
      const pending = <Pending text={text} />;
      const reference = eInvoiceId ? (
        <a
          href={ORDER_ACCOUNTING_DOCUMENT_META.e_invoice.href(eInvoiceId)}
          title={eInvoiceId}
        >
          Mã nội bộ: {internalInvoiceNumber ?? '--'}
        </a>
      ) : null;
      const pendingContent = (
        <Space direction="vertical" size={2}>
          {pending}
          {reference}
        </Space>
      );
      // HĐĐT đã tồn tại (kể cả còn ở draft/pending) thì không cho gửi lại yêu
      // cầu; duplicate request sẽ chỉ làm người dùng thấy lỗi 409.
      if (eInvoice?.invoice_id || eInvoice?.requested) return pendingContent;
      if (!onRequestEInvoice) return pending;
      return (
        <Space size={8} wrap>
          {pendingContent}
          <Button
            size="small"
            icon={<SendOutlined />}
            loading={isRequestingEInvoice}
            onClick={onRequestEInvoice}
          >
            Gửi yêu cầu
          </Button>
        </Space>
      );
    }

    if (row.type === 'goods_issue') {
      const fallbackCode = stockIssueCode ?? posGoodsIssueCode;
      if (!fallbackCode) return <Pending text={row.pending} />;
      if (!posGoodsIssueId) return <Text strong>{fallbackCode}</Text>;
      return <DocCode doc={{ type: 'goods_issue', doc_id: posGoodsIssueId, code: fallbackCode }} />;
    }

    return <Pending text={row.pending} />;
  };

  return (
    <Card title="Chứng từ kế toán" size="small">
      <Descriptions
        column={1}
        size="small"
        bordered={false}
        styles={{ label: { color: '#666', width: 120 } }}
      >
        {ROWS.map((row) => (
          <Descriptions.Item key={row.type} label={row.label}>
            {renderRow(row)}
          </Descriptions.Item>
        ))}
        {carrierReconciliation && (
          <Descriptions.Item label="Đối soát ĐVVC">
            <Space direction="vertical" size={0}>
              <Text type={carrierReconciliation.completed ? 'success' : undefined}>
                {carrierReconciliation.completed
                  ? 'Đã hoàn thành đối soát'
                  : 'Chưa hoàn thành đối soát'}
              </Text>
              {carrierReconciliation.completed && carrierReconciliation.occurredAt && (
                <Text type="secondary">
                  Hoàn thành lúc: {formatDateTime(carrierReconciliation.occurredAt)}
                </Text>
              )}
              {carrierReconciliation.completed && carrierReconciliation.statementId && (
                <Text type="secondary">
                  Mã bảng kê: {carrierReconciliation.statementId}
                </Text>
              )}
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>

      {canRetry && onRetryAccounting && (
        <Popconfirm
          title="Thử lại kế toán?"
          description="Thao tác sẽ tiếp tục xử lý kế toán và không xoá các chứng từ đã ghi sổ."
          okText="Xác nhận"
          cancelText="Hủy"
          onConfirm={() => { void onRetryAccounting(); }}
        >
          <Button
            type="primary"
            size="small"
            aria-label="Thử lại kế toán"
            loading={isRetryingAccounting}
            disabled={isRetryingAccounting}
          >
            Thử lại kế toán
          </Button>
        </Popconfirm>
      )}

      {canRetryScenario && onRetryScenario && (
        <Popconfirm
          title="Thử lại kịch bản chứng từ?"
          description="Thao tác sẽ tiếp tục kịch bản CTBH/PXK/HĐĐT nháp từ bước còn dang dở, không tạo lại các chứng từ đã ghi sổ."
          okText="Xác nhận"
          cancelText="Hủy"
          onConfirm={() => { void onRetryScenario(); }}
        >
          <Button
            size="small"
            aria-label="Thử lại kịch bản chứng từ"
            loading={isRetryingScenario}
            disabled={isRetryingScenario}
            style={{ marginLeft: canRetry ? 8 : 0 }}
          >
            Thử lại kịch bản chứng từ
          </Button>
        </Popconfirm>
      )}

      {accountingStatus === 'failed' && !isCostingDeferred && accountingError && (
        <Text type="danger" style={{ display: 'block', marginTop: 8, overflowWrap: 'anywhere' }}>
          {accountingError}
        </Text>
      )}

      {accounting?.sales_invoice_error && (
        <Text type="danger" style={{ display: 'block', marginTop: 8, overflowWrap: 'anywhere' }}>
          {accounting.sales_invoice_error}
        </Text>
      )}
    </Card>
  );
};

export default OrderAccountingCard;
